import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowDown,
  ArrowUp,
  GripVertical,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useId, useState } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/cn";
import { useEncryptOnSave, useGoalShareTargets } from "~/lib/crypto";
import type { KeyManager } from "~/lib/crypto";
import type { DecryptedMilestone } from "~/lib/queries/milestoneQuery";
import {
  useAddMilestone,
  useDeleteMilestone,
  useMoveMilestone,
  useRenameMilestone,
} from "~/lib/queries/milestoneQuery";
import { MILESTONE_TITLE_MAX_LENGTH } from "~/lib/schemas/goal-of-life";

import type { LifeGoal } from "./goal-groups";

/*
 * A step's controls take their names from its visible title by reference.
 * Its decrypted title never sits in an attribute, where click tracking
 * (Sentry breadcrumbs, the Meta pixel) would read and send it.
 */
const STEP_ACTIONS_LABEL_ID = "goal-step-actions-label";
const DRAG_LABEL_ID = "goal-step-drag-label";

function StepControlLabels() {
  return (
    <>
      <span id={STEP_ACTIONS_LABEL_ID} hidden>
        Actions for step
      </span>
      <span id={DRAG_LABEL_ID} hidden>
        Drag to reorder
      </span>
    </>
  );
}

/** A step the client typed that is not saved yet; its text stays until it is. */
type PendingStep = { id: string; title: string; failed: boolean };

/**
 * Each step is saved on its own, with its own key: a new or renamed title is
 * encrypted for the owner, the coaches the goals are shared with, and the
 * coach of each program the goal is in. When the goal has a public link, the
 * plain title is published in the same save.
 */
function useStepActions(goal: LifeGoal, keyManager: KeyManager | null) {
  const { encryptFields } = useEncryptOnSave(keyManager);
  const goalShareTargets = useGoalShareTargets();
  const addMutation = useAddMilestone();
  const renameMutation = useRenameMilestone();

  const encryptTitle = async (title: string) => {
    const encrypted = await encryptFields(
      { title },
      await goalShareTargets(goal.id),
    );
    return {
      encryptedTitle: encrypted.fields.title,
      iv: encrypted.iv,
      keyGrants: encrypted.keyGrants,
      ...(goal.shareSlug ? { shareTitle: title } : {}),
    };
  };

  return {
    add: async (id: string, title: string) =>
      addMutation.mutateAsync({
        goalId: goal.id,
        id,
        ...(await encryptTitle(title)),
      }),
    rename: async (id: string, title: string) =>
      renameMutation.mutateAsync({ id, ...(await encryptTitle(title)) }),
  };
}

function StepMenu({
  titleId,
  canRename,
  isFirst,
  isLast,
  onRename,
  onMove,
  onRemove,
}: {
  /** The id of the step's visible title, which names the menu. */
  titleId: string;
  canRename: boolean;
  isFirst: boolean;
  isLast: boolean;
  onRename: () => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-labelledby={`${STEP_ACTIONS_LABEL_ID} ${titleId}`}
          // Shown on hover or focus on a pointer screen, always on a phone.
          className="sm:opacity-0 sm:group-focus-within/step:opacity-100 sm:group-hover/step:opacity-100 sm:data-[state=open]:opacity-100"
        >
          <MoreHorizontal aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        // Rename puts a field where the menu was; focus stays in it.
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        {canRename && (
          <DropdownMenuItem onSelect={onRename}>
            <Pencil aria-hidden />
            Rename step
          </DropdownMenuItem>
        )}
        {!isFirst && (
          <DropdownMenuItem onSelect={() => onMove(-1)}>
            <ArrowUp aria-hidden />
            Move up
          </DropdownMenuItem>
        )}
        {!isLast && (
          <DropdownMenuItem onSelect={() => onMove(1)}>
            <ArrowDown aria-hidden />
            Move down
          </DropdownMenuItem>
        )}
        <DropdownMenuItem variant="destructive" onSelect={onRemove}>
          <Trash2 aria-hidden />
          Remove step
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Renames a step in place: Enter saves, Escape cancels. */
function RenameField({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (title: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <Input
      autoFocus
      aria-label="Step name"
      value={value}
      onValueChange={(next) =>
        setValue(next.slice(0, MILESTONE_TITLE_MAX_LENGTH))
      }
      onBlur={onCancel}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          onCancel();
        }
        if (event.key === "Enter") {
          event.preventDefault();
          const title = value.trim();
          if (title && title !== initial) onSave(title);
          else onCancel();
        }
      }}
      className="mb-0 h-9"
    />
  );
}

function StepRow({
  step,
  checked,
  readOnly,
  isFirst,
  isLast,
  onToggle,
  onRename,
  onMove,
  onRemove,
}: {
  step: DecryptedMilestone;
  checked: boolean;
  readOnly: boolean;
  isFirst: boolean;
  isLast: boolean;
  onToggle: (checked: boolean) => void;
  onRename: (title: string) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
}) {
  const [isRenaming, setIsRenaming] = useState(false);
  const titleId = useId();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id, disabled: readOnly });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        "group/step flex min-h-11 items-center gap-2 rounded-lg px-2 py-1 hover:bg-gray-50",
        isDragging && "relative z-10 bg-white opacity-80 shadow-sm",
      )}
    >
      <div className="min-w-0 flex-1">
        {isRenaming ? (
          <RenameField
            initial={step.title}
            onCancel={() => setIsRenaming(false)}
            onSave={(title) => {
              setIsRenaming(false);
              onRename(title);
            }}
          />
        ) : (
          <Checkbox
            checked={checked}
            disabled={readOnly}
            onCheckedChange={(value) => onToggle(value === true)}
          >
            <span
              id={titleId}
              className={cn(
                "text-sm break-words",
                checked ? "text-text-tertiary line-through" : "text-gray-900",
              )}
            >
              {step.title}
            </span>
          </Checkbox>
        )}
      </div>
      {!readOnly && !isRenaming && (
        <>
          <StepMenu
            titleId={titleId}
            canRename={step.isReadable}
            isFirst={isFirst}
            isLast={isLast}
            onRename={() => setIsRenaming(true)}
            onMove={onMove}
            onRemove={onRemove}
          />
          <button
            type="button"
            aria-labelledby={`${DRAG_LABEL_ID} ${titleId}`}
            // A drag handle, not a button look; phones use the menu's Move up and Move down.
            className="hidden cursor-grab touch-none rounded p-1 text-gray-400 hover:bg-gray-100 sm:block"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="size-4" aria-hidden />
          </button>
        </>
      )}
    </li>
  );
}

/** The step count and bar: "2 of 5 done". */
export function StepProgress({ total, done }: { total: number; done: number }) {
  if (total === 0) return null;
  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-gray-100">
      <div
        className="h-full rounded-full bg-purple-600 transition-[width]"
        style={{ width: `${(done / total) * 100}%` }}
      />
    </div>
  );
}

/**
 * A goal's steps. The owner ticks them, adds one with Enter, renames, moves
 * and removes them, and each change saves at once. A coach reads them.
 */
export function GoalSteps({
  goal,
  steps,
  readOnly,
  keyManager,
  ticked,
  onTick,
}: {
  goal: LifeGoal;
  steps: DecryptedMilestone[];
  readOnly: boolean;
  keyManager: KeyManager | null;
  /** Whether each step shows ticked, including ticks still saving. */
  ticked: (step: DecryptedMilestone) => boolean;
  onTick: (step: DecryptedMilestone, checked: boolean) => void;
}) {
  const actions = useStepActions(goal, keyManager);
  const moveMutation = useMoveMilestone();
  const deleteMutation = useDeleteMilestone();
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState<PendingStep[]>([]);
  // The order the client just made, until the saved order comes back.
  const [movedOrder, setMovedOrder] = useState<{
    ids: string[];
    from: DecryptedMilestone[];
  } | null>(null);

  // A step whose save reached the server, even when its reply was lost, is
  // in the list now.
  const unsaved = pending.filter(
    (step) => !steps.some((saved) => saved.id === step.id),
  );
  const ordered =
    movedOrder && movedOrder.from === steps
      ? movedOrder.ids.flatMap((id) => steps.find((s) => s.id === id) ?? [])
      : steps;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const saveStep = async (step: PendingStep) => {
    setPending((list) =>
      list.map((p) => (p.id === step.id ? { ...p, failed: false } : p)),
    );
    try {
      await actions.add(step.id, step.title);
      setPending((list) => list.filter((p) => p.id !== step.id));
    } catch (error) {
      console.error("Failed to add a goal step", { goalId: goal.id, error });
      setPending((list) =>
        list.map((p) => (p.id === step.id ? { ...p, failed: true } : p)),
      );
    }
  };

  const addDraft = () => {
    const title = draft.trim();
    if (!title) return;
    const step = { id: uuidv4(), title, failed: false };
    setPending((list) => [...list, step]);
    setDraft("");
    void saveStep(step);
  };

  const move = (id: string, to: number) => {
    const from = ordered.findIndex((s) => s.id === id);
    if (from === -1 || to < 0 || to >= ordered.length || from === to) return;
    setMovedOrder({
      ids: arrayMove(
        ordered.map((s) => s.id),
        from,
        to,
      ),
      from: steps,
    });
    moveMutation.mutate(
      { id, newOrder: to },
      {
        onError: () => {
          setMovedOrder(null);
          toast.error("The new order was not saved.");
        },
      },
    );
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    move(
      String(active.id),
      ordered.findIndex((s) => s.id === over.id),
    );
  };

  const rename = (id: string, title: string) => {
    actions.rename(id, title).catch((error: unknown) => {
      console.error("Failed to rename a goal step", { stepId: id, error });
      toast.error("The step was not renamed.");
    });
  };

  const remove = (id: string) => {
    deleteMutation.mutate(id, {
      onError: () => toast.error("The step was not removed."),
    });
  };

  return (
    <div className="space-y-2">
      <StepControlLabels />
      {ordered.length === 0 && unsaved.length === 0 && (
        <p className="text-text-tertiary text-sm">
          {readOnly
            ? "No steps yet."
            : "No steps yet. Break the goal into steps you can tick off."}
        </p>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={ordered.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <ul className="-mx-2 space-y-1">
            {ordered.map((step, index) => (
              <StepRow
                key={step.id}
                step={step}
                checked={ticked(step)}
                readOnly={readOnly}
                isFirst={index === 0}
                isLast={index === ordered.length - 1}
                onToggle={(checked) => onTick(step, checked)}
                onRename={(title) => rename(step.id, title)}
                onMove={(by) => move(step.id, index + by)}
                onRemove={() => remove(step.id)}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      {unsaved.length > 0 && (
        <ul className="space-y-1">
          {unsaved.map((step) => (
            <li
              key={step.id}
              className="flex min-h-11 flex-wrap items-center justify-between gap-2 text-sm"
            >
              <span className="break-words text-gray-900">{step.title}</span>
              {step.failed ? (
                <span className="flex items-center gap-2 text-red-700">
                  Not saved
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto px-0 text-red-700"
                    onClick={() => void saveStep(step)}
                  >
                    Retry
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="text-text-tertiary h-auto px-0"
                    onClick={() =>
                      setPending((list) => list.filter((p) => p.id !== step.id))
                    }
                  >
                    Discard
                  </Button>
                </span>
              ) : (
                <span className="text-text-tertiary">Saving…</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <div className="relative">
          <Plus
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <Input
            aria-label="Add a step"
            placeholder="Add a step"
            value={draft}
            // Without maxLength: the input's character counter would crowd the row.
            onValueChange={(next) =>
              setDraft(next.slice(0, MILESTONE_TITLE_MAX_LENGTH))
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addDraft();
              }
            }}
            className="mb-0 pl-9"
          />
        </div>
      )}
    </div>
  );
}
